import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Spin } from "antd";
import "firebase/auth";
import Auth from "../../api/auth";
import TwoFAModal from "./components/TwoFAModal";
import FactorsModal from "./components/FactorsModal";
import { Redirect } from "react-router-dom";
import { withTheme } from "styled-components";
import { useIntl } from "react-intl";
import LoginTemplate from "../../components/templates/LoginTemplate";
import login from "../../assets/dreams_logo.png";
import { setTwoFAResolved, setIs2FA } from "./UserSlice";
import { isMobile } from "react-device-detect";
import useTenantDetails from "../../hooks/useTenantDetails";
import useTwoFactorAuth from "../../hooks/useTwoFactorAuth";

import LoginForm from "./components/LoginForm";

import {
  TotpMultiFactorGenerator,
  PhoneMultiFactorGenerator,
} from "firebase/auth";

const ERROR_CODES = [
  "auth/wrong-password",
  "auth/invalid-email",
  "auth/invalid-action-code",
  "auth/too-many-requests",
];

const SignIn = (props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { hasProvider, providerTenant, providerName, isLoadingTenant } =
    useTenantDetails();
  const {
    twoFactorAuthError,
    twoFactorAuthLoading,
    resendCodeLoading,
    setTwoFactorAuthCode,
    initiateTwoFactorAuthentication,
    verifyTwoFactorAuthCode,
    resendTwoFactorAuthCode,
    twoFactorAuthCode,
    handleCancel,
    twoFactorCodeModalvisible,
  } = useTwoFactorAuth();
  const { profile, twoFAResolved, is2FA } = useSelector((state) => state.user);
  const [authError, setAuthError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [factorsModalVisible, setFactorsModalVisible] = useState(false);
  const [authResult, setAuthResult] = useState(null);
  const [activeFactorId, setActiveFactorId] = useState(
    TotpMultiFactorGenerator.FACTOR_ID
  );

  const recaptchaRef = useRef();
  const urlParams = new URLSearchParams(window.location.search);
  const system_user = urlParams.get("system_user");
  const isSystemUser = system_user === "true";

  const handleAuthError = (err) => {
    return ERROR_CODES.includes(err.code) ? err.code : err.message;
  };

  const handle2FA = () => {
    const hints = authResult?.resolver?.hints;
    switch (hints?.length) {
      case 2:
        // Initiate TOTP factor authentication if there are two factors
        initiateTwoFactorAuthentication(
          TotpMultiFactorGenerator.FACTOR_ID,
          authResult
        );
        setActiveFactorId(TotpMultiFactorGenerator.FACTOR_ID);
        break;

      case 1:
        // If there is only one factor, initiate it (e.g., to support SMS authentication)
        const [singleHint] = hints;
        initiateTwoFactorAuthentication(singleHint.factorId, authResult);
        setActiveFactorId(singleHint.factorId);
        break;

      default:
        // Handle other cases, like when there are no hints
        console.warn("Invalid number of authentication factors.");
        break;
    }
  };

  const switchToMobileAuthantication = () => {
    console.log(authResult);
    initiateTwoFactorAuthentication(
      PhoneMultiFactorGenerator.FACTOR_ID,
      authResult
    );
    setActiveFactorId(PhoneMultiFactorGenerator.FACTOR_ID);
  };

  const hasMobileFactor = !!authResult?.resolver?.hints.find(
    (factor) => factor.factorId === PhoneMultiFactorGenerator.FACTOR_ID
  );

  useEffect(() => {
    if (authResult?.recaptchaVerifier && authResult?.resolver) {
      handle2FA();
    }
  }, [authResult]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const result = await Auth.signInWithEmailAndPassword(
        values.username,
        values.password,
        isSystemUser,
        recaptchaRef.current,
        authResult?.recaptchaVerifier
      );
      setAuthResult(result);
      if (result.is2FA) {
        dispatch(setIs2FA(true));
      } else {
        dispatch(setIs2FA(false));
      }
      if (result.error) {
        setAuthError(handleAuthError(result.error));
      }
    } catch (err) {
      setAuthError(handleAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const onCodeChange = (value) => {
    setTwoFactorAuthCode(value);
  };

  const onValuesChange = (changedValues, allValues) => {
    if (authError) {
      setAuthError(null);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  if (isMobile && !window.location.host.includes("localhost")) {
    let url = window.location.origin;
    url = url.replace(/.a-dreams/, ".static.a-dreams");
    window.location.replace(url);
  }

  const onLoginWithProvider = async () => {
    setLoading(true);
    await Auth.signInWithProvider(providerName, providerTenant);
    setLoading(false);
  };
  if (profile && (twoFAResolved || is2FA === false || hasProvider)) {
    return <Redirect to="/" />;
  }

  return (
    <LoginTemplate
      showForgot={true}
      title={intl.formatMessage({
        id: "app.forms.user.login_title",
      })}
      subTitle={
        !hasProvider &&
        intl.formatMessage({
          id: "app.forms.user.login_subtitle",
        })
      }
      image={<img src={login} alt={"logo"} style={{ maxWidth: "400px" }} />}
    >
      {isLoadingTenant ? (
        <Spin size="large" />
      ) : hasProvider && !isSystemUser ? (
        <Button
          loading={loading}
          block
          type="primary"
          onClick={onLoginWithProvider}
        >
          {intl.formatMessage({
            id: "app.actions.connect",
          })}
        </Button>
      ) : (
        <LoginForm
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          onValuesChange={onValuesChange}
          loading={loading}
          authError={authError}
          recaptchaRef={recaptchaRef}
        />
      )}
      <TwoFAModal
        visible={twoFactorCodeModalvisible}
        handleCancel={handleCancel}
        phoneNumber={
          authResult?.resolver && authResult?.resolver.hints[0].phoneNumber
        }
        activeFactorId={activeFactorId}
        onCodeChange={onCodeChange}
        onSubmitCode={() =>
          verifyTwoFactorAuthCode(
            activeFactorId === PhoneMultiFactorGenerator.FACTOR_ID
              ? "confirm2FAPhoneCode"
              : "confirm2FATotpCode",
            authResult
          )
        }
        onResend={resendTwoFactorAuthCode}
        twoFAError={twoFactorAuthError}
        twoFACode={twoFactorAuthCode}
        loading={twoFactorAuthLoading}
        resendLoading={resendCodeLoading}
        hasMobileFactor={hasMobileFactor}
        switchToMobileAuthantication={switchToMobileAuthantication}
      />
      <FactorsModal
        factors={authResult?.resolver?.hints}
        activeFactorId={activeFactorId}
        visible={factorsModalVisible}
        handleCancel={() => {
          setFactorsModalVisible(false);
          setActiveFactorId(PhoneMultiFactorGenerator.FACTOR_ID);
        }}
        setActiveFactorId={(e) => {
          setActiveFactorId(e.target.value);
        }}
        onOk={() => {
          initiateTwoFactorAuthentication(activeFactorId, authResult);
          setFactorsModalVisible(false);
        }}
      />
    </LoginTemplate>
  );
};

export default withTheme(SignIn);
